// MarkdownPreview.jsx - Split-pane Markdown editor with live preview
import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MarkdownPreview = ({ content, onChange }) => {
  return (
    <Box sx={{ display: 'flex', gap: 2, height: '100%', overflow: 'hidden' }}>
      {/* Markdown Editor Pane */}
      <Box
        sx={{
          flex: 1,
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          Markdown Editor
        </Typography>
        <TextField
          multiline
          fullWidth
          value={content}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type Markdown here...

# Heading 1
## Heading 2

**Bold text** and *italic text*

- List item 1
- List item 2

[Link text](https://example.com)

```code block```
"
          variant="outlined"
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              height: '100%',
              alignItems: 'flex-start',
              fontFamily: 'monospace',
              fontSize: '14px',
              padding: 0
            },
            '& .MuiInputBase-input': {
              height: '100% !important',
              overflow: 'auto !important',
              padding: '16px'
            },
            '& fieldset': {
              border: 'none'
            }
          }}
        />
      </Box>

      {/* Live Preview Pane */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          Preview
        </Typography>
        <Box
          sx={{
            flex: 1,
            p: 2,
            overflow: 'auto',
            '& h1': { fontSize: '2em', fontWeight: 600, mt: 0, mb: 2 },
            '& h2': { fontSize: '1.5em', fontWeight: 600, mt: 3, mb: 1.5 },
            '& h3': { fontSize: '1.25em', fontWeight: 600, mt: 2, mb: 1 },
            '& p': { mb: 2 },
            '& ul, & ol': { mb: 2, pl: 3 },
            '& li': { mb: 0.5 },
            '& code': {
              bgcolor: 'grey.100',
              p: 0.5,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.9em'
            },
            '& pre': {
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              mb: 2
            },
            '& pre code': {
              bgcolor: 'transparent',
              p: 0
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            },
            '& blockquote': {
              borderLeft: '4px solid',
              borderColor: 'grey.300',
              pl: 2,
              ml: 0,
              fontStyle: 'italic',
              color: 'text.secondary'
            }
          }}
        >
          {content.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content}
            </ReactMarkdown>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Start typing Markdown on the left to see the preview here...
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MarkdownPreview;
